import { useState, useEffect, useRef } from "react";
import { LuPlus } from "react-icons/lu";
import API_URL from "../Config";
import { useNavigate } from "react-router-dom";
import { IoEye } from "react-icons/io5";
import axiosInstance from "../axiosinstance";
import SearchBar from "../Components/SearchBar.js";
import PaginationFooter from "../Components/PaginationFooter.js";
import "./ProjectTable.css";
import { FaUser } from "react-icons/fa";
import ViewProjectDetail from "../View/ViewProjectDetail";
import {
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "../Components/Loader";
function ProjectTable({ isSidebarExpanded }) {
  const [Projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCertificateId, setSelectedCertificateId] = useState(null); // State to track the selected certificate
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
    const isInitialLoad = useRef(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [users, setUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // <-- track query
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  useEffect(() => {
    if (isSearching && searchQuery) {
      fetchSearchProjects(searchQuery, currentPage);
    } else {
      fetchData(false, currentPage);
    }
  }, [currentPage, isSearching, searchQuery]);

  const fetchSearchProjects = async (query, page = 1) => {
    try {
      const res = await axiosInstance.get(`${API_URL}/api/search_project`, {
        params: { query, page, Items_per_page: itemsPerPage },
      });

      const { total_records, data } = res.data;
      setProjects(res.data.data || []);
      setTotalRecords(total_records ?? 0);
    } catch (err) {
      console.error("Search fetch failed:", err.response?.data || err.message);
      const status = err.response?.status;
      if (status === 403) {
        const userRole = localStorage.getItem("role_name");
        if (userRole?.toLowerCase() === "field technician") {
          setErrorMessage("Field Technicians have limited access to project search. Contact your administrator for full access.");
        } else {
          setErrorMessage("Admin access required to search projects.");
        }
      } else {
        setErrorMessage("Failed to search projects");
      }
    }
  };

  const fetchData = async (retry = true, page = 1) => {
    setLoading(true);
     if (isInitialLoad.current) {
      setPageLoading(true);
    }
    try {
      const response = await axiosInstance.get(
        `${API_URL}/api/get_all_project`,
        {
          params: {
            page,
            items_per_page: itemsPerPage,
          },
        }
      );
      const { total_records, data } = response.data;
      setProjects(data || []);
      setTotalRecords(total_records ?? 0);
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Something went wrong";

      if (status === 403) {
        const userRole = localStorage.getItem("role_name");
        if (userRole?.toLowerCase() === "field technician") {
          setErrorMessage("Field Technicians have limited access to project data. Contact your administrator for full access.");
        } else {
          setErrorMessage("Admin access required to view all projects.");
        }
        return;
      }

      if (status === 401) {
        setErrorMessage("Please log in again to access projects.");
        return;
      }

      setErrorMessage("Failed to load projects");
    } finally {
      setTimeout(() => {
           if (isInitialLoad.current) {
          setPageLoading(false);
          isInitialLoad.current = false;
        }
        setLoading(false);
        }, 1000);
    }
  };

  const AddUser = () => {
    navigate("/project-form");
  };

  const userRole = localStorage.getItem("role_name")?.toLowerCase();
  const canCreateProject = userRole !== "field technician";

  const handleNavigate = (id) => {
    navigate(`/Edit-project-form/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch (error) {
      return "N/A";
    }
  };

  return (
    <>
      <div>
        {pageLoading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(6px)",
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                transform: "scale(2)",
                gap: "10px",
              }}
            >
              <Loader />
            </div>
          </div>
        )}
        <div
          className={`table-container project-page ${
            isSidebarExpanded ? "expanded" : "collapsed"
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="project-page-content">
            <div className="project-page-header">
              <div className="project-header-titleblock">
                <h2 className="page-title mb-0">Projects</h2>
              </div>
              <div className="project-header-buttons d-flex align-items-center">
                <div className="project-header-controls-row">
                  <div className="project-search-inline">
                    <SearchBar
                      placeholder="Search Projects..."
                      apiEndpoint={`${API_URL}/api/search_project`}
                      resultKey="data"
                      onResults={({ data, totalRecords, isSearch, query }) => {
                        setProjects(data);
                        if (typeof totalRecords !== "undefined")
                          setTotalRecords(totalRecords);
                        setIsSearching(isSearch);
                        setSearchQuery(query);
                        setCurrentPage(1);
                      }}
                      fetchDefaultData={() => fetchData()}
                    />
                  </div>

                  {canCreateProject && (
                    <button className="project-new-button" onClick={AddUser}>
                      <LuPlus />
                      <span>New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="project-error-message">
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "18px" }}>⚠️</span>
                  <span>{errorMessage}</span>
                </div>
              </div>
            )}

            <div className="project-table-card">
              <div className="project-table-wrapper">
                <table className="project-data-table">
                  <colgroup>
                    <col className="project-col-sr" />
                    <col className="project-col-name" />
                    <col className="project-col-client" />
                    <col className="project-col-unit" />
                    <col className="project-col-location" />
                    <col className="project-col-start-date" />
                    <col className="project-col-end-date" />
                    <col className="project-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Name</th>
                      <th>Client Name</th>
                      <th>Unit Name</th>
                      <th>Location</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 10 }).map((_, rowIdx) => (
                        <tr key={`project-skeleton-${rowIdx}`}>
                          {Array.from({ length: 8 }).map((__, colIdx) => (
                            <td key={colIdx}>
                              <div
                                style={{
                                  height: "10px",
                                  background: "#e5e8ee",
                                  borderRadius: "3px",
                                  animation: "pulse 2s ease-in-out infinite",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : Projects.length > 0 ? (
                      Projects.map((project, index) => (
                        <tr key={project.project_id || index}>
                          <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            <span
                              className="project-name"
                              onClick={() => {
                                setSelectedProjectId(project.project_id);
                                setViewModalOpen(true);
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {project.name || "N/A"}
                            </span>
                          </td>
                          <td>
                            <Tooltip
                              title={
                                <>
                                  <div>
                                    <strong>Client Code:</strong> {project.client_code || "N/A"}
                                  </div>
                                  <div>
                                    <strong>Location:</strong> {project.location || "N/A"}
                                  </div>
                                </>
                              }
                              arrow
                              placement="bottom"
                            >
                              <span style={{ cursor: "pointer" }}>
                                {project.client_name || "N/A"}
                              </span>
                            </Tooltip>
                          </td>
                          <td>{project.unit_name || "N/A"}</td>
                          <td>{project.location || "N/A"}</td>
                          <td>{formatDate(project.start_date)}</td>
                          <td>{formatDate(project.end_date)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <Tooltip
                                title={canCreateProject ? "Edit" : "Field Technicians cannot edit projects"}
                              >
                                <button
                                  className="project-action-btn"
                                  onClick={() => handleNavigate(project.project_id)}
                                  disabled={!canCreateProject}
                                  style={{
                                    opacity: canCreateProject ? 1 : 0.5,
                                    cursor: canCreateProject ? "pointer" : "not-allowed",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                              </Tooltip>
                              <Tooltip title="View">
                                <button
                                  className="project-action-btn"
                                  onClick={() => {
                                    setSelectedProjectId(project.project_id);
                                    setViewModalOpen(true);
                                  }}
                                >
                                  <IoEye style={{ fontSize: "18px" }} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} style={{ textAlign: "center", padding: "24px" }}>
                          <Typography variant="body2">No data available</Typography>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationFooter
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
              />
            </div>
          </div>
        </div>

        <ViewProjectDetail
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedProjectId(null);
          }}
          projectId={selectedProjectId}
        />
      </div>
    </>
  );
}

export default ProjectTable;
